/* eslint-disable max-statements */
import { Button, Group, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useState } from 'react';

import { GoodLocation, GoodLocationCriteria } from '@e-origin/shared';

import { deleteIcon, viewIcon } from '@assets';
import { SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { defaultGoodLocationCriteria, useDeclarant, useDeleteGoodLocation, useGoodLocation } from '@hooks/declarant';
import { EditGoodLocation } from './EditGoodLocation';

export const GoodsLocation = () => {
  const { data: declarant } = useDeclarant();
  const [filters, setFilters] = useState<GoodLocationCriteria['filters']>(defaultGoodLocationCriteria.filters);
  const [sorting, setSorting] = useState<GoodLocationCriteria['sorting']>(defaultGoodLocationCriteria.sorting);
  const [pagination, setPagination] = useState<GoodLocationCriteria['pagination']>(defaultGoodLocationCriteria.pagination);

  // refactor: sometimes declarant is not loading fast enough (cannot read _id of undefined)
  const { data: { data: goodsLocations, totalItems } = {} } = useGoodLocation(declarant?._id, { filters, sorting, pagination });
  const deleteGoodLocationMutation = useDeleteGoodLocation(declarant?._id);

  const locationTypes = [
    { value: 'A', label: 'A - Designated location' },
    { value: 'B', label: 'B - Authorised place' },
    { value: 'C', label: 'C - Approved place' },
    { value: 'D', label: 'D - Other' },
  ];

  const qualifierTypes = [
    { value: 'T', label: 'T - Postcode address' },
    { value: 'U', label: 'U - UN/LOCODE' },
    { value: 'V', label: 'V - Customs office identifier' },
    { value: 'W', label: 'W - GNSS coordinates' },
    { value: 'X', label: 'X - EORI number' },
    { value: 'Y', label: 'Y - Authorisation number' },
    { value: 'Z', label: 'Z - Address' },
  ];

  const columns: TableColumnProps<GoodLocation>[] = [
    {
      name: 'UNLOCODE/Warehouse ID',
      cell: (row) => row.unLocode,
      sortable: true,
      selector: 'unLocode',
    },
    {
      name: 'UNLOCODE Description',
      cell: (row) => (row.unLocodeDescription || '').slice(0, 20),
      sortable: true,
      selector: 'unLocodeDescription',
    },
    {
      name: 'Type',
      cell: (row) => locationTypes.find((item) => item.value === row.type)?.label || row.type,
      sortable: true,
      selector: 'type',
    },
    {
      name: 'Qualifier',
      cell: (row) => qualifierTypes.find((item) => item.value === row.qualifierOfIdentification)?.label || row.type,
      sortable: true,
      selector: 'qualifierOfIdentification',
    },
    {
      name: 'Postcode',
      cell: (row) => row.virtuals?.displayedPostcode as string,
    },
    {
      name: 'City',
      cell: (row) => row.virtuals?.displayedCity as string,
    },
    {
      name: 'Default Location',
      cell: (row) => (row.defaultLocation ? 'True' : 'False'),
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => (
        <TableActions
          actions={[
            {
              icon: viewIcon,
              text: 'View Chart',
              onClick: () => openGoodsLocationModal(row),
            },
            {
              icon: deleteIcon,
              text: 'Delete',
              onClick: () => openDeleteGoodsLocationModal(row),
            },
          ]}
        />
      ),
    },
  ];

  const openDeleteGoodsLocationModal = (goodLocation: GoodLocation) => {
    modals.openConfirmModal({
      title: 'Delete location of goods',
      children: <div>Are you sure you want to delete this location of goods?</div>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red', loading: deleteGoodLocationMutation.isPending },
      onConfirm: async () => {
        await deleteGoodLocationMutation.mutateAsync(goodLocation._id);
        modals.closeAll();
      },
    });
  };

  const openGoodsLocationModal = (goodLocation?: GoodLocation) => {
    modals.open({
      title: goodLocation ? 'Edit location of good' : 'New location of goods',
      children: <EditGoodLocation show={true} onHide={() => modals.closeAll()} goodLocationToEdit={goodLocation} />,
      size: 'lg',
    });
  };

  const handleSearch = (searchQuery: string) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  return (
    <Stack>
      <Group justify="flex-end" mb="md">
        <Button onClick={() => openGoodsLocationModal()} variant="outline" size="lg">
          Add new
        </Button>
        <SearchBox placeholder="Search by UNLOCODE or description" handleSearch={handleSearch} value={filters?.searchQuery} />
      </Group>
      <Table
        columns={columns}
        dataSource={goodsLocations}
        total={totalItems}
        onPaginationChange={handlePagination}
        onSortingChange={handleSorting}
        defaultPage={pagination.page}
        defaultPerPage={pagination.size}
        defaultSorting={sorting.field}
        defaultSortingDirection={sorting.direction}
        verticalSpacing="xs"
      />
    </Stack>
  );
};
